import { Headphones } from 'lucide-react'
import { useId, useState } from 'react'
import { useArticleAudio, type ArticleAudioKind } from '../../hooks/useArticleAudio'

function displayVoice(value: string): string {
  const voice = value.trim()
  return voice ? `${voice[0].toUpperCase()}${voice.slice(1)}` : 'Sage'
}

function preparingPlayer(labelId: string, compact: boolean, message = 'Audio edition preparing.') {
  const label = 'AI-NARRATED PODCAST · SAGE'
  return <aside className={`article-audio-player article-audio-player--preparing ${compact ? 'article-audio-player--compact' : ''}`.trim()} aria-labelledby={labelId}>
    <Headphones aria-hidden="true"/>
    <div><strong id={labelId}>{label}</strong><p>{message}</p></div>
  </aside>
}

export function ArticleAudioPlayer({ kind, id, title, variant = 'default' }: { kind: ArticleAudioKind; id: string; title: string; variant?: 'default' | 'compact' }) {
  const [refreshKey, setRefreshKey] = useState(0)
  const audio = useArticleAudio(kind, id, true, refreshKey)
  const labelId = useId()
  const descriptionId = useId()
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const compact = variant === 'compact'

  if (audio.status === 'checking' || audio.status === 'unavailable') return null
  if (audio.status === 'preparing') return preparingPlayer(labelId, compact)
  if (failedUrl === audio.episode.audioUrl) return preparingPlayer(labelId, compact, 'Audio playback temporarily unavailable. Refresh to try again.')

  const voice = displayVoice(audio.episode.voice)
  const minutes = audio.episode.durationSeconds === undefined ? undefined : Math.max(1, Math.round(audio.episode.durationSeconds / 60))
  const isState = kind === 'state'
  const Heading = compact ? 'h3' : 'h2'
  return <section className={`article-audio-player ${compact ? 'article-audio-player--compact' : ''}`.trim()} aria-labelledby={labelId} aria-describedby={descriptionId}>
    <div className="article-audio-player__heading">
      <Headphones aria-hidden="true"/>
      <div><span>{isState ? 'LISTEN TO THIS EDITION' : 'LISTEN TO THIS ARTICLE'}</span><Heading id={labelId}>AI-narrated podcast edition</Heading></div>
    </div>
    <p id={descriptionId}>Read by an AI-generated {voice} voice. The complete source-linked {isState ? 'State of AI' : 'article'} remains on this page.</p>
    <audio controls preload="metadata" src={audio.episode.audioUrl} aria-label={`Listen to ${title}`} onError={() => { setFailedUrl(audio.episode.audioUrl); setRefreshKey((value) => value + 1) }}>
      Your browser cannot play this audio. <a href={audio.episode.audioUrl}>Open the audio file instead.</a>
    </audio>
    {audio.episode.transcriptUrl && <a className="article-audio-player__transcript" href={audio.episode.transcriptUrl}>Read the exact audio transcript</a>}
    <div className="article-audio-player__meta"><span>VOICE · {voice.toUpperCase()}</span>{minutes && <span>ABOUT {minutes} MIN</span>}<span>NO AUTOPLAY</span></div>
  </section>
}
